import React from "react";
import { InferType, object, string } from "yup";
import { Form, Formik, Field, FieldProps } from "collections/Form";
import { Loader, SubmitButton } from "elements";
import { FormInput } from "routes/Manage/Sites/Settings/GeneralSiteSettings";
import { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { Layout } from "views/Main/Layouts";
import { Redirect } from "polyrouter";
import {
  useDeviceGroupsQuery,
  useUpdateDeviceGroupMutation
} from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";
import { needs, useGuard } from "rbac";
import BlockingForm from "collections/Form/BlockingForm";
import { useDeviceGroup } from "../DeviceGroupsUtils";
import { SettingsCategory } from "../../Inventory/Settings-Shared/DeviceSettingsComponents";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export const DeviceGroupSettingsSchema = object({
  groupName: string()
    .label("NAME")
    .required()
    .ensure()
});

export const DeviceGroupTitle: React.FC<{
  groupName: string;
  userCanEdit: boolean;
}> = ({ groupName, userCanEdit }) => {
  const { t } = useTranslation();

  const { Content } = Layout;
  return (
    <div className="grid">
      <div className="col-6 col-xl-8">
        <Content.Title className="font-xxl">
          {t("SETTINGS")}{" "}
          <p className="inline-block font-light">{`| ${groupName}`}</p>
        </Content.Title>
      </div>
      <div className="col-6 col-xl-4 text-right">
        {userCanEdit && (
          <>
            <Field name="reset">
              {({ form: { dirty, handleReset, isSubmitting } }: FieldProps) => (
                <button
                  type="button"
                  className="btn"
                  disabled={isSubmitting || !dirty}
                  onClick={handleReset}
                >
                  {t("DISCARD")}
                </button>
              )}
            </Field>
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  isSubmitting={isSubmitting}
                  name="submit"
                  disabled={!dirty || !isValid}
                />
              )}
            </Field>
          </>
        )}
      </div>
    </div>
  );
};

type GeneralSettings = InferType<typeof DeviceGroupSettingsSchema>;

export const DeviceGroupSettings: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { Content } = Layout;
  const tenantId = useTenantId();
  const userCanEdit = useGuard(needs("UPDATE_SITES"));

  const { data: groupData, fetching: groupFetching } = useDeviceGroup(id);

  const { data: allDeviceGroupDataByTenant } = useDeviceGroupsQuery({
    variables: {
      tenantId
    }
  });
  const [, updateGroup] = useUpdateDeviceGroupMutation();
  const [addNotification] = useNotification();

  if (nameIsUnassigned(groupData?.name)) {
    return <Redirect to="/manage/device-groups" />;
  }

  const initialValues = {
    groupName: groupData?.name || "",
    groupId: groupData?.id || ""
  };

  const handleSubmit = async (values: GeneralSettings) => {
    var response = await updateGroup({
      groupId: groupData?.id!,
      groupName: values.groupName,
      createSWPolicy: false
    });

    if (response.data?.updateGroup?.error) {
      addNotification({
        type: "error",
        message: t("ERROR_UPDATING_DEVICE_GROUP"),
        dismiss: DISMISS.DEFAULT
      });
      return;
    }

    addNotification({
      type: "success",
      message: t("SUCCESSFULLY_UPDATE_DEVICE"),
      dismiss: DISMISS.DEFAULT
    });
  };

  const checkDuplicates = (values: GeneralSettings) => {
    let errors: any = {};
    const allGroups = allDeviceGroupDataByTenant?.getAllGroups.groups?.groups;

    if (values.groupName !== groupData?.name) {
      const deviceGroup = allGroups?.find(
        group => group.name === values.groupName
      );

      if (deviceGroup) {
        errors["groupName"] = t("ERROR_DUPLICATE_DEVICE_GROUP", {
          groupName: values?.groupName
        });
      }
    }
    return errors;
  };

  return (
    <Content className="settings">
      <Loader loading={groupFetching}>
        <Formik
          name="DeviceGroupSettings"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={DeviceGroupSettingsSchema}
          validate={checkDuplicates}
          enableReinitialize
        >
          {({ dirty }) => {
            return (
              <BlockingForm unsavedChanges={dirty}>
                <Form className="form grid" data-testid="DeviceGroupSettings">
                  <DeviceGroupTitle
                    groupName={initialValues.groupName}
                    userCanEdit={userCanEdit}
                  />
                  <SettingsCategory title={t("GENERAL")}>
                    <FormInput
                      label={t("DEVICE_GROUP_NAME")}
                      name="groupName"
                      tabIndex={0}
                      disabled={!userCanEdit}
                    />
                  </SettingsCategory>
                </Form>
              </BlockingForm>
            );
          }}
        </Formik>
      </Loader>
    </Content>
  );
};

export default DeviceGroupSettings;
